<script setup lang="ts">
import { computed, inject, onMounted, ref } from "vue";
import {
  FormElementType,
  LoginAccountInSysOrgDto,
  SysOrgRoleName,
  UserSelectFormElement,
} from "~/api";
import { useLoginAccountsStore } from "~/stores/LoginAccountsStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import {
  FormResponseState,
  formResponseInjectKey,
} from "../../composables/useFormResponse";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props = defineProps<DefaultFormComponentProps<UserSelectFormElement>>();

const { useAnswer, formResponse } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
const answer = useAnswer<FormElementType.USER_SELECT>(props.modelValue);
const elementProperties = computed(() => props.modelValue.properties);
const myInfoStore = useMyInfoStore();
const loginAccountsStore = useLoginAccountsStore();

const users = ref<LoginAccountInSysOrgDto[]>([]);
const loading = ref(false);

onMounted(async () => {
  loading.value = true;
  try {
    const roleName: SysOrgRoleName =
      (elementProperties.value.roleName as SysOrgRoleName) ??
      SysOrgRoleName.MEMBER;
    await loginAccountsStore.load(false, roleName);
    let fetchedUsers = loginAccountsStore.getList(roleName);

    // Reorder users based on form creator and current answer
    fetchedUsers = reorderUsers(fetchedUsers);

    users.value = fetchedUsers;
  } finally {
    loading.value = false;
  }
});

const reorderUsers = (
  userList: LoginAccountInSysOrgDto[],
): LoginAccountInSysOrgDto[] => {
  if (!formResponse?.createdByUserGuid) return userList;

  const formCreatorGuid = formResponse.createdByUserGuid;
  const currentUserGuid = myInfoStore.userInfo.userGuid;
  const currentAnswer = answer.value;

  // If there's an answer: [answer, currentUser, formCreator]
  // Otherwise:            [formCreator, currentUser]
  const priorityGuids = [
    ...new Set(
      (currentAnswer
        ? [currentAnswer, currentUserGuid, formCreatorGuid]
        : [formCreatorGuid, currentUserGuid]
      ).filter(Boolean),
    ),
  ];

  const priorityUsers = priorityGuids
    .map((g) => userList.find((u) => u.userGuid === g))
    .filter((u): u is LoginAccountInSysOrgDto => !!u);

  const prioritySet = new Set(priorityUsers.map((u) => u.userGuid));
  const rest = userList.filter((u) => !prioritySet.has(u.userGuid));

  return [...priorityUsers, ...rest];
};

const getDisplayLabel = (user: LoginAccountInSysOrgDto): string => {
  if (user.loginName && user.email) {
    return `${user.loginName} (${user.email})`;
  }
  return user.loginName ?? user.email ?? "Unknown";
};
</script>

<template>
  <div class="UserSelectComponent">
    <slot name="label" :element="modelValue" />

    <div style="flex-grow: 1"></div>

    <el-select
      :model-value="answer ?? undefined"
      :placeholder="elementProperties.placeholder ?? 'Select a user'"
      :disabled="isReadOnly"
      :loading="loading"
      filterable
      @update:model-value="answer = $event ?? null"
    >
      <el-option
        v-for="user in users"
        :key="user.userGuid"
        :label="getDisplayLabel(user)"
        :value="user.userGuid"
      >
        <span>{{ user.loginName ?? user.email }}</span>
        <span
          v-if="user.email && user.loginName"
          style="
            margin-left: 8px;
            color: var(--el-text-color-secondary);
            font-size: 0.85em;
          "
        >
          {{ user.email }}
        </span>
      </el-option>
    </el-select>
  </div>
</template>

<style lang="less">
.UserSelectComponent {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;

  .el-select__selection {
    text-align: right;
  }
  .el-select {
    .el-select__wrapper.is-disabled,
    .el-select__wrapper.is-disabled.is-hovering {
      box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
      background-color: #f2f2f2;
      .el-select__placeholder {
        color: #444142;
      }
    }
  }
}

body.forPdf .UserSelectComponent {
  .el-select {
    width: fit-content;
    .el-select__selection {
      text-align: right;
    }
    .el-select__wrapper {
      box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
      background-color: transparent !important;
      .el-select__placeholder {
        color: black !important;
      }
    }
  }
  .el-select__wrapper {
    box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
    background-color: transparent !important;
    .el-select__placeholder {
      color: black !important;
    }
  }
}
</style>
